/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button as CDSButton, Typography2, breakpoints } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import type { EntWebsiteHeroContent } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';
import { useBlockLayoutTheme } from 'bundles/ent-website/hooks/useBlockLayoutTheme';
import DataConsumerWrapper from 'bundles/ent-website/utils/DataConsumerWrapper';

import _t from 'i18n!nls/ent-website';

const styles = {
  list: css`
    color: inherit;
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin: var(--cds-spacing-300) 0;
  `,
  listItem: css`
    display: flex;
    gap: var(--cds-spacing-150);

    p {
      color: inherit;
    }
  `,
  bodyText: css`
    color: inherit;
    margin: var(--cds-spacing-300) 0;

    p {
      font-size: var(--cds-font-size-200) !important;
    }
  `,
  buttonGroup: css`
    margin: var(--cds-spacing-300) 0 var(--cds-spacing-150) 0;
    display: flex;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      margin-left: auto;
      margin-right: auto;
      align-items: center;

      button {
        width: 100%;
        max-width: 100%;
      }
    }
  `,
  subCtaBase: css`
    p {
      color: inherit;
      color: var(--cds-color-neutral-primary-weak) !important;
      font-size: var(--cds-font-size-175) !important;
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  subCtaTextInverted: css`
    p {
      color: var(--cds-color-neutral-primary-invert) !important;
    }
  `,
};

type Props = {
  tabContent: EntWebsiteHeroContent;
  module: BlockLevelModule;
};

const ContentBody = ({ tabContent, module }: Props) => {
  const { isInverted } = useBlockLayoutTheme();
  const { paragraph, bulletPoints, primaryCta, subCtaText, secondaryCta, heroContentDisplayStyle } = tabContent;

  const renderContent = () => {
    if (heroContentDisplayStyle === 'bullet-points' && bulletPoints?.length) {
      return (
        <ul css={styles.list}>
          {bulletPoints.map((item) => (
            <li key={item} css={styles.listItem}>
              <div>
                <CheckIcon color={isInverted ? 'invert' : 'interactive'} />
              </div>
              <Typography2 variant="bodyPrimary" component="p">
                {item}
              </Typography2>
            </li>
          ))}
        </ul>
      );
    } else if (heroContentDisplayStyle === 'paragraph' && paragraph) {
      return (
        <DataConsumerWrapper template={paragraph}>
          {({ result, isLoading }) =>
            isLoading ? (
              <div>{_t('Loading...')}</div>
            ) : (
              <Markdown css={styles.bodyText} source={result} module={module} />
            )
          }
        </DataConsumerWrapper>
      );
    }
    return null;
  };

  return (
    <div>
      {renderContent()}

      <div css={styles.buttonGroup}>
        <DataConsumerWrapper template={primaryCta?.ctaTarget}>
          {({ result, isLoading }) => {
            return isLoading ? (
              <CDSButton variant={isInverted ? 'primaryInvert' : 'primary'} disabled>
                {_t('Loading...')}
              </CDSButton>
            ) : (
              <ContentfulButton
                module={module}
                position={0}
                variant={isInverted ? 'primaryInvert' : 'primary'}
                component="a"
                href={result || '#'}
              >
                {primaryCta?.ctaText}
              </ContentfulButton>
            );
          }}
        </DataConsumerWrapper>
        {secondaryCta && (
          <DataConsumerWrapper template={secondaryCta?.ctaTarget}>
            {({ result, isLoading }) => {
              return isLoading ? (
                <CDSButton variant={isInverted ? 'ghostInvert' : 'secondary'} disabled>
                  {_t('Loading...')}
                </CDSButton>
              ) : (
                <ContentfulButton
                  module={module}
                  position={1}
                  variant={isInverted ? 'ghostInvert' : 'secondary'}
                  component="a"
                  href={result || '#'}
                >
                  {secondaryCta?.ctaText}
                </ContentfulButton>
              );
            }}
          </DataConsumerWrapper>
        )}
      </div>

      <DataConsumerWrapper template={subCtaText}>
        {({ result, isLoading }) => {
          return isLoading ? (
            <div>{_t('Loading...')}</div>
          ) : (
            <Markdown
              css={[styles.subCtaBase, isInverted ? styles.subCtaTextInverted : '']}
              source={result}
              module={module}
            />
          );
        }}
      </DataConsumerWrapper>
    </div>
  );
};

export default ContentBody;
